import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ExtraWorkOrdersApiAdapter } from '../adapters';
import { ExtraWorkOrdersFilters, ExtraWorkOrder } from '../models';

export const useExtraWorkOrders = (filters: ExtraWorkOrdersFilters = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new ExtraWorkOrdersApiAdapter(httpClient);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
  } = useInfiniteQuery({
    queryKey: ['extra-work-orders', filters],
    queryFn: ({ pageParam = 1 }) =>
      apiAdapter.getExtraWorkOrders({
        ...filters,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.current_page < lastPage.meta.last_page) {
        return lastPage.meta.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    staleTime: 1 * 60 * 1000, // 1 minuto
    placeholderData: (previousData) => previousData,
  });

  const orders: ExtraWorkOrder[] =
    data?.pages.flatMap((page) => page.data) ?? [];
  const total = data?.pages?.[0]?.meta?.total ?? 0;

  return {
    orders,
    isLoading: isLoading || isPending,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    total,
  };
};
